import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import * as Icon from 'react-feather';
import { formatMoney, validateAddress, whatsappOrderUrl } from '@loop-coffee/commerce-contracts';

import { itemCount, useCommerce } from '../../commerce/CommerceContext';
import { PaymentOptions } from '../../commerce/payments/PaymentOptions';
import { describePaymentStatus } from '../../commerce/payments/registry';
import LogoLight from '../../assets/images/logo-light.png';

const font = "'Space Grotesk', sans-serif";
const mono = "'Space Grotesk', monospace";
const panel = { background: '#111', borderRadius: 8, padding: 24, fontFamily: font };
const contactLink = whatsappOrderUrl('Olá! Vim pelo site da Loop Coffee e preciso de ajuda com meu pedido.');

const STEP_LABELS = { cart: 'carrinho', address: 'entrega', review: 'revisão', confirmation: 'confirmação' };

function Alert({ children, tone = 'error', role = 'alert' }) {
    const background = tone === 'error' ? 'rgba(190,30,45,0.2)' : 'rgba(109,46,145,0.25)';
    return (
        <div role={role} className="p-3 mb-3 rounded text-white" style={{ background, fontSize: 14 }}>
            {children}
        </div>
    );
}

function Steps({ current }) {
    return (
        <ol className="d-flex flex-wrap gap-3 list-unstyled mb-4" aria-label="Etapas do checkout" style={{ fontFamily: mono, fontSize: 12 }}>
            {Object.entries(STEP_LABELS).map(([step, label]) => (
                <li key={step} aria-current={step === current ? 'step' : undefined}
                    style={{ color: step === current ? '#BE1E2D' : 'rgba(255,255,255,0.5)' }}>
                    {label}()
                </li>
            ))}
        </ol>
    );
}

function Totals({ cart }) {
    const money = (amount) => formatMoney(amount, cart.currency_code);
    return (
        <dl className="mb-0" style={{ fontSize: 14 }}>
            <div className="d-flex justify-content-between text-white-50">
                <dt className="fw-normal">Produtos</dt>
                <dd className="mb-1">{money(cart.item_total ?? cart.subtotal)}</dd>
            </div>
            {cart.shipping_methods?.length > 0 && (
                <div className="d-flex justify-content-between text-white-50">
                    <dt className="fw-normal">Frete</dt>
                    <dd className="mb-1">{money(cart.shipping_total)}</dd>
                </div>
            )}
            {cart.tax_total > 0 && (
                <div className="d-flex justify-content-between text-white-50">
                    <dt className="fw-normal">Impostos</dt>
                    <dd className="mb-1">{money(cart.tax_total)}</dd>
                </div>
            )}
            <div className="d-flex justify-content-between text-white fw-bold">
                <dt>Total</dt>
                <dd className="mb-0" data-testid="cart-total">{money(cart.total)}</dd>
            </div>
        </dl>
    );
}

function CartStep({ onNext }) {
    const { cart, status, notice, error, pending, restore, updateQuantity, removeItem } = useCommerce();

    if (status === 'loading') {
        return <p role="status" className="text-white-50">Carregando carrinho…</p>;
    }
    if (status === 'error') {
        return (
            <Alert>
                Não foi possível carregar seu carrinho ({error?.message}). Seus itens continuam salvos.
                <div className="mt-2">
                    <button type="button" className="btn btn-sm btn-outline-light" onClick={restore}>Tentar novamente</button>
                </div>
            </Alert>
        );
    }

    const items = cart?.items || [];
    return (
        <>
            {notice === 'expired' && (
                <Alert tone="info" role="status">
                    Seu carrinho anterior expirou ou já foi finalizado. Escolha os cafés novamente.
                </Alert>
            )}
            {error && <Alert>{error.message}</Alert>}
            {items.length === 0 ? (
                <div style={panel}>
                    <p className="text-white mb-3">Seu carrinho está vazio.</p>
                    <Link to="/#bitbean" className="btn btn-primary btn-sm">Ver cafés</Link>
                </div>
            ) : (
                <div style={panel}>
                    <ul className="list-unstyled mb-4" aria-label="Itens do carrinho">
                        {items.map((item) => (
                            <li key={item.id} className="d-flex flex-wrap justify-content-between align-items-center gap-3 py-3"
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <div>
                                    <div className="text-white fw-semibold">{item.product_title || item.title}</div>
                                    <small className="text-white-50">
                                        {item.variant_title} · {formatMoney(item.unit_price, cart.currency_code)} / un.
                                    </small>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <button type="button" className="btn btn-sm btn-outline-light" disabled={pending}
                                        aria-label={`Diminuir ${item.title}`} onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                                        <Icon.Minus size={14} />
                                    </button>
                                    <span className="text-white" aria-label={`Quantidade de ${item.title}`} style={{ minWidth: 24, textAlign: 'center' }}>
                                        {item.quantity}
                                    </span>
                                    <button type="button" className="btn btn-sm btn-outline-light" disabled={pending}
                                        aria-label={`Aumentar ${item.title}`} onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                                        <Icon.Plus size={14} />
                                    </button>
                                    <button type="button" className="btn btn-sm btn-link text-white-50" disabled={pending}
                                        aria-label={`Remover ${item.title}`} onClick={() => removeItem(item.id)}>
                                        <Icon.Trash2 size={14} />
                                    </button>
                                </div>
                                <span className="text-white">{formatMoney(item.total ?? item.unit_price * item.quantity, cart.currency_code)}</span>
                            </li>
                        ))}
                    </ul>
                    <Totals cart={cart} />
                    <button type="button" className="btn btn-primary w-100 mt-4" disabled={pending} onClick={onNext}>
                        Continuar para entrega
                    </button>
                </div>
            )}
        </>
    );
}

const ADDRESS_INPUTS = [
    { name: 'email', label: 'E-mail', type: 'email', autoComplete: 'email' },
    { name: 'first_name', label: 'Nome', autoComplete: 'given-name' },
    { name: 'last_name', label: 'Sobrenome', autoComplete: 'family-name' },
    { name: 'phone', label: 'Telefone (opcional)', type: 'tel', autoComplete: 'tel' },
    { name: 'postal_code', label: 'CEP', autoComplete: 'postal-code' },
    { name: 'address_1', label: 'Endereço e número', autoComplete: 'address-line1' },
    { name: 'address_2', label: 'Complemento (opcional)', autoComplete: 'address-line2' },
    { name: 'city', label: 'Cidade', autoComplete: 'address-level2' },
    { name: 'province', label: 'Estado (UF)', autoComplete: 'address-level1' },
];

function AddressStep({ onBack, onNext }) {
    const { cart, error, pending, setAddress } = useCommerce();
    const [values, setValues] = useState(() => {
        const saved = cart?.shipping_address || {};
        return Object.fromEntries(ADDRESS_INPUTS.map(({ name }) => [name, (name === 'email' ? cart?.email : saved[name]) || '']));
    });
    const [errors, setErrors] = useState({});

    const submit = async (event) => {
        event.preventDefault();
        const found = validateAddress(values);
        setErrors(found);
        if (Object.keys(found).length) return;
        if (await setAddress(values)) onNext();
    };

    return (
        <form noValidate onSubmit={submit} style={panel} aria-label="Endereço de entrega">
            {error && <Alert>{error.message}</Alert>}
            <Row>
                {ADDRESS_INPUTS.map(({ name, label, type = 'text', autoComplete }) => (
                    <Col md={6} key={name} className="mb-3">
                        <label htmlFor={`address-${name}`} className="form-label text-white-50" style={{ fontSize: 13 }}>{label}</label>
                        <input
                            id={`address-${name}`}
                            name={name}
                            type={type}
                            autoComplete={autoComplete}
                            className={`form-control${errors[name] ? ' is-invalid' : ''}`}
                            value={values[name]}
                            aria-invalid={Boolean(errors[name])}
                            aria-describedby={errors[name] ? `address-${name}-error` : undefined}
                            onChange={(e) => { const { value } = e.target; setValues((prev) => ({ ...prev, [name]: value })); }}
                        />
                        {errors[name] && <div id={`address-${name}-error`} className="invalid-feedback">{errors[name]}</div>}
                    </Col>
                ))}
            </Row>
            <div className="d-flex gap-2 mt-2">
                <button type="button" className="btn btn-outline-light" onClick={onBack}>Voltar ao carrinho</button>
                <button type="submit" className="btn btn-primary flex-grow-1" disabled={pending}>Revisar pedido</button>
            </div>
        </form>
    );
}

function ShippingSlot() {
    const { cart, shipping, selectShipping, pending } = useCommerce();
    const [state, setState] = useState({ status: 'loading', options: [] });
    const cartId = cart?.id;

    useEffect(() => {
        let active = true;
        setState({ status: 'loading', options: [] });
        shipping.listOptions({ id: cartId }).then(
            (options) => active && setState({ status: options.length ? 'ready' : 'empty', options }),
            () => active && setState({ status: 'error', options: [] }),
        );
        return () => { active = false; };
    }, [shipping, cartId]);

    const selected = cart.shipping_methods?.[0]?.shipping_option_id;

    if (state.status === 'loading') return <p role="status" className="text-white-50">Calculando opções de envio…</p>;
    if (state.status === 'error') return <Alert>Não foi possível carregar as opções de envio.</Alert>;
    if (state.status === 'empty') return <Alert>Nenhuma opção de envio para este endereço.</Alert>;

    return (
        <fieldset className="mb-3">
            <legend className="text-white" style={{ fontSize: 15 }}>Envio</legend>
            {state.options.map((option) => (
                <div className="form-check" key={option.id}>
                    <input className="form-check-input" type="radio" name="shipping-option" id={`shipping-${option.id}`}
                        checked={selected === option.id} disabled={pending} onChange={() => selectShipping(option.id)} />
                    <label className="form-check-label text-white-50" htmlFor={`shipping-${option.id}`}>
                        {option.label} — {formatMoney(option.amount, cart.currency_code)}
                    </label>
                </div>
            ))}
        </fieldset>
    );
}

function PaymentSlot() {
    const { listPaymentOptions, payment, selectPayment, pending } = useCommerce();
    const [state, setState] = useState({ status: 'loading', options: [] });

    useEffect(() => {
        let active = true;
        setState({ status: 'loading', options: [] });
        listPaymentOptions().then(
            ({ options }) => {
                if (!active) return;
                setState({ status: 'ready', options });
                if (options.length === 1) selectPayment(options[0].id);
            },
            () => active && setState({ status: 'error', options: [] }),
        );
        return () => { active = false; };
    }, [listPaymentOptions, selectPayment]);

    if (state.status === 'loading') return <p role="status" className="text-white-50">Carregando formas de pagamento…</p>;
    if (state.status === 'error') return <Alert>Não foi possível carregar as formas de pagamento.</Alert>;

    return (
        <div data-testid="payment-slot" data-provider={payment?.id || ''} style={{ fontSize: 14 }}>
            <PaymentOptions options={state.options} selectedId={payment?.id} onSelect={selectPayment} disabled={pending} />
        </div>
    );
}

function ReviewStep({ onBack, onEditCart, onPlaced }) {
    const { cart, error, pending, payment, placeOrder } = useCommerce();

    if (!cart?.items?.length) {
        return (
            <Alert>
                Seu carrinho mudou e está vazio.{' '}
                <button type="button" className="btn btn-sm btn-outline-light ms-2" onClick={onEditCart}>Voltar ao carrinho</button>
            </Alert>
        );
    }

    const address = cart.shipping_address || {};
    const place = async () => {
        if (await placeOrder()) onPlaced();
    };

    return (
        <div style={panel}>
            {error && (
                <Alert>
                    {error.message} Os preços e o estoque foram conferidos novamente pela loja.
                    <div className="mt-2">
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={onEditCart}>Revisar carrinho</button>
                    </div>
                </Alert>
            )}
            <h2 className="text-white h6">Itens</h2>
            <ul className="list-unstyled text-white-50 mb-3" style={{ fontSize: 14 }}>
                {cart.items.map((item) => (
                    <li key={item.id}>{item.quantity}× {item.product_title || item.title} — {formatMoney(item.total ?? item.unit_price * item.quantity, cart.currency_code)}</li>
                ))}
            </ul>
            <h2 className="text-white h6">Entrega</h2>
            <p className="text-white-50 mb-3" style={{ fontSize: 14 }}>
                {address.first_name} {address.last_name} · {address.address_1}{address.address_2 ? `, ${address.address_2}` : ''} · {address.city}/{address.province?.toUpperCase()} · {address.postal_code}
                <br />
                <button type="button" className="btn btn-link btn-sm p-0" onClick={onBack}>Alterar endereço</button>
            </p>
            <ShippingSlot />
            <h2 className="text-white h6">Pagamento</h2>
            <PaymentSlot />
            <Totals cart={cart} />
            <button type="button" className="btn btn-primary w-100 mt-4"
                disabled={pending || !cart.shipping_methods?.length || !payment} onClick={place}>
                {pending ? 'Enviando…' : 'Finalizar pedido'}
            </button>
        </div>
    );
}

function Confirmation() {
    const { order, payment } = useCommerce();
    const status = describePaymentStatus(order?.payment_status);
    return (
        <div style={panel} role="status">
            <Icon.CheckCircle size={32} color="#BE1E2D" className="mb-3" />
            <h2 className="text-white h4">Pedido {order?.display_id ? `#${order.display_id}` : ''} recebido</h2>
            <p className="text-white" data-testid="payment-status" data-state={status.state}>{status.label}</p>
            <p className="text-white-50">
                {status.isPaid ? '' : 'Avisaremos assim que o pagamento for confirmado. '}
                Enviaremos as próximas etapas para {order?.email}.
                {payment?.testOnly && ' Ambiente de teste: nenhuma cobrança real foi feita.'}
            </p>
            <Link to="/" className="btn btn-outline-light btn-sm">Voltar à loja</Link>
        </div>
    );
}

function Unavailable() {
    return (
        <div style={panel}>
            <p className="text-white">A compra online ainda não está disponível.</p>
            <a href={contactLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">Comprar pelo WhatsApp</a>
        </div>
    );
}

export default function Checkout() {
    const { enabled, cart } = useCommerce();
    const [step, setStep] = useState('cart');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    // Address and review need an open cart; an expired/emptied cart sends the customer back.
    const current = step !== 'confirmation' && step !== 'cart' && !cart ? 'cart' : step;

    return (
        <main style={{ background: '#0a0a0a', minHeight: '100vh', paddingTop: 40, paddingBottom: 80 }}>
            <Container>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <Link to="/"><img src={LogoLight} alt="Loop Coffee" height="60" /></Link>
                    <Link to="/" className="text-white-50" style={{ fontFamily: mono, fontSize: 13 }}>
                        <Icon.ArrowLeft size={14} className="me-1" />voltar_à_loja()
                    </Link>
                </div>
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <h1 className="text-white mb-3" style={{ fontFamily: font, fontWeight: 700, fontSize: '2rem' }}>
                            Checkout {enabled && current === 'cart' && cart ? <small className="text-white-50 fs-6">({itemCount(cart)} itens)</small> : null}
                        </h1>
                        {!enabled ? <Unavailable /> : (
                            <>
                                <Steps current={current} />
                                {current === 'cart' && <CartStep onNext={() => setStep('address')} />}
                                {current === 'address' && <AddressStep onBack={() => setStep('cart')} onNext={() => setStep('review')} />}
                                {current === 'review' && (
                                    <ReviewStep onBack={() => setStep('address')} onEditCart={() => setStep('cart')}
                                        onPlaced={() => setStep('confirmation')} />
                                )}
                                {current === 'confirmation' && <Confirmation />}
                            </>
                        )}
                        <p className="text-white-50 mt-4" style={{ fontSize: 12 }}>
                            Textos e políticas provisórios, sujeitos a revisão. Dúvidas? <a href={contactLink} target="_blank" rel="noopener noreferrer">Fale conosco</a>.
                        </p>
                    </Col>
                </Row>
            </Container>
        </main>
    );
}
